/**
 * Google Places Autocomplete (New) API Provider
 *
 * Implements the SuggestionProvider interface using Google's Places API (New).
 *
 * API Documentation: https://developers.google.com/maps/documentation/places/web-service/place-autocomplete
 *
 * Features:
 * - Address autocomplete with structured format
 * - Session token support for billing grouping
 * - Location bias with radius
 * - Region code and place type filtering
 * - Language code support
 */

import type {
  SuggestionProvider,
  SuggestRequest,
  SuggestResult,
  AddressSuggestion,
} from '../../types/suggestion'
import { SuggestionError } from '../../types/common'
import type { GoogleClient } from './client'

const AUTOCOMPLETE_API_URL =
  'https://places.googleapis.com/v1/places:autocomplete'

const FIELD_MASK = [
  'suggestions.placePrediction.placeId',
  'suggestions.placePrediction.text',
  'suggestions.placePrediction.structuredFormat',
  'suggestions.placePrediction.types',
  'suggestions.placePrediction.distanceMeters',
].join(',')

/** Google Places Autocomplete (New) request body */
interface GoogleAutocompleteRequest {
  input: string
  sessionToken?: string
  locationBias?: {
    circle: {
      center: { latitude: number; longitude: number }
      radius: number
    }
  }
  includedPrimaryTypes?: string[]
  includedRegionCodes?: string[]
  languageCode?: string
}

/** Google Places Autocomplete (New) response */
interface GoogleAutocompleteResponse {
  suggestions?: GoogleSuggestion[]
  error?: {
    code: number
    message: string
    status: string
  }
}

interface GoogleSuggestion {
  placePrediction: {
    placeId: string
    text?: { text: string }
    structuredFormat?: {
      mainText?: { text: string }
      secondaryText?: { text: string }
    }
    types?: string[]
    distanceMeters?: number
  }
}

export class GoogleSuggestionProvider implements SuggestionProvider {
  readonly name = 'google_places_autocomplete'
  private client: GoogleClient

  constructor(client: GoogleClient) {
    this.client = client
  }

  async suggest(request: SuggestRequest): Promise<SuggestResult> {
    const body = this.buildRequestBody(request)

    const response = await this.client.post<GoogleAutocompleteResponse>(
      AUTOCOMPLETE_API_URL,
      body,
      {
        headers: {
          'X-Goog-FieldMask': FIELD_MASK,
        },
      }
    )

    this.validateResponse(response, request.input)

    return {
      predictions: (response.suggestions ?? []).map((s) =>
        this.mapToCanonical(s)
      ),
      source: this.name,
      suggestedAt: new Date().toISOString(),
    }
  }

  private buildRequestBody(
    request: SuggestRequest
  ): GoogleAutocompleteRequest {
    const body: GoogleAutocompleteRequest = {
      input: request.input,
    }

    if (request.sessionToken) {
      body.sessionToken = request.sessionToken
    }

    if (request.locationBias) {
      body.locationBias = {
        circle: {
          center: {
            latitude: request.locationBias.center.latitude,
            longitude: request.locationBias.center.longitude,
          },
          radius: request.locationBias.radiusMeters ?? 50000,
        },
      }
    }

    if (request.includedTypes?.length) {
      body.includedPrimaryTypes = request.includedTypes
    }

    if (request.includedRegionCodes?.length) {
      body.includedRegionCodes = request.includedRegionCodes
    }

    if (request.language) {
      body.languageCode = request.language
    }

    return body
  }

  private validateResponse(
    response: GoogleAutocompleteResponse,
    input: string
  ): void {
    if (response.error) {
      throw new SuggestionError(
        response.error.message,
        response.error.status,
        input,
        { googleError: response.error }
      )
    }
  }

  private mapToCanonical(suggestion: GoogleSuggestion): AddressSuggestion {
    const { placePrediction: p } = suggestion
    const mainText = p.structuredFormat?.mainText?.text ?? ''
    const secondaryText = p.structuredFormat?.secondaryText?.text ?? ''
    const fullText = p.text?.text ?? [mainText, secondaryText].filter(Boolean).join(', ')

    return {
      placeId: p.placeId,
      mainText,
      secondaryText,
      fullText,
      types: p.types ?? [],
      distanceMeters: p.distanceMeters,
    }
  }
}
